import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {fetchTopRepositories, type RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {noop} from '@github-ui/noop'
import {issuePath} from '@github-ui/paths'
import {type RefObject, useEffect, useRef, useState} from 'react'
import {fetchQuery, graphql, useRelayEnvironment} from 'react-relay'

import {VALUES} from '../constants/values'
import {IssueCreateContextProvider} from '../contexts/IssueCreateContext'
import type {CreateIssueProps} from '../CreateIssue'
import type {CreateIssueCallbackProps} from '../CreateIssueForm'
import type {TemplateListSharedProps} from '../TemplateList'
import {DisplayMode} from '../utils/display-mode'
import type {OnCreateProps} from '../utils/model'
import type {IssueCreateOptionConfig, SafeOptionConfig} from '../utils/option-config'
import {getSafeConfig} from '../utils/option-config'
import {constructIssueCreateParams} from '../utils/template-args'
import type {
  CreateIssueDialogEntryQuery,
  CreateIssueDialogEntryQuery$data,
} from './__generated__/CreateIssueDialogEntryQuery.graphql'
import {CreateIssueDialog} from './CreateIssueDialog'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const CreateIssueDialogEntryGraphQLQuery = graphql`
  query CreateIssueDialogEntryQuery($owner: String!, $name: String!, $includeTemplates: Boolean = false) {
    repository(owner: $owner, name: $name) {
      hasAnyTemplates
      isBlankIssuesEnabled
      hasIssuesEnabled
      id
      databaseId
      name
      nameWithOwner
      owner {
        databaseId
        login
        avatarUrl(size: 64)
        issueTypesEnabled
      }
      isLocked
      isPrivate
      isOwnerEnterpriseManaged
      visibility
      isArchived
      isInOrganization
      slashCommandsEnabled
      viewerCanPush
      viewerCanCreateIssues
      viewerInteractionLimitReasonHTML(action: "create an issue")
      viewerIssueCreationPermissions {
        labelable
        milestoneable
        assignable
        triageable
        typeable
        canSetIssueFields
      }
      shortDescriptionHTML
      ...CreateIssueDialog @arguments(includeTemplates: $includeTemplates)
    }
  }
`

export type CreateIssueDialogEntryProps = {
  navigate: (url: string) => void
  isCreateDialogOpen: boolean
  setIsCreateDialogOpen: (value: boolean) => void
  optionConfig?: IssueCreateOptionConfig
  returnFocusRef?: RefObject<HTMLElement | null>
  fallbackElement?: React.ReactElement
} & Partial<CreateIssueCallbackProps> &
  TemplateListSharedProps

export const CreateIssueDialogEntry = ({
  navigate,
  isCreateDialogOpen,
  setIsCreateDialogOpen,
  onCancel,
  onCreateSuccess,
  onCreateError,
  optionConfig,
  isNavigatingToNew,
  setIsNavigatingToNew,
  returnFocusRef,
  fallbackElement,
}: CreateIssueDialogEntryProps): React.ReactElement | null => {
  const environment = useRelayEnvironment()
  const [topRepositories, setTopRepositories] = useState<RepositoryPickerRepository[] | null>(null)
  const [currentRepository, setCurrentRepository] = useState<CreateIssueDialogEntryQuery$data | null>(null)

  const config = getSafeConfig(optionConfig)
  const shouldLoadTopRepos = config.showRepositoryPicker
  const shouldLoadCurrentRepo = config.issueCreateArguments?.repository ? true : false

  useEffect(() => {
    const fetchTopRepos = async () => {
      const repos = await fetchTopRepositories(environment, {
        first: VALUES.repositoriesPreloadCount,
        hasIssuesEnabled: true,
      })
      setTopRepositories(repos)
    }

    const fetchCurrentRepo = async () => {
      const repository = config.issueCreateArguments?.repository
      if (!repository) return
      const queryData = await fetchQuery<CreateIssueDialogEntryQuery>(
        environment,
        CreateIssueDialogEntryGraphQLQuery,
        {
          owner: repository.owner,
          name: repository.name,
          includeTemplates: true, // TODO: remove this param entirely
        },
        {fetchPolicy: 'store-or-network'},
      ).toPromise()
      setCurrentRepository(queryData ?? null)
    }

    if (shouldLoadCurrentRepo) {
      fetchCurrentRepo()
    }
    if (shouldLoadTopRepos) {
      fetchTopRepos()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDialogClose = () => {
    onCancel?.()
    setIsCreateDialogOpen(false)
  }

  const handleOnCreate = ({issue, createMore}: OnCreateProps) => {
    // Respect the callback if it exists, else use default behaviour.
    if (onCreateSuccess) {
      onCreateSuccess({issue, createMore})
    } else if (!createMore) {
      handleDialogClose()
      navigate(
        issuePath({
          owner: issue.repository.owner.login,
          repo: issue.repository.name,
          issueNumber: issue.number,
        }),
      )
    }
  }

  const handleOnError = (error: Error) => onCreateError?.(error)

  const createIssueDialogProps: CreateIssuePropsEntry = {
    topRepositories: shouldLoadTopRepos ? topRepositories : 'unset',
    onCreateSuccess: handleOnCreate,
    onCreateError: handleOnError,
    onCancel: handleDialogClose,
    navigate,
    isNavigatingToNew,
    setIsNavigatingToNew,
  }

  if (!isCreateDialogOpen) {
    return null
  }

  return (
    <CreateIssueEntryWrapper
      createIssueProps={createIssueDialogProps}
      config={config}
      currentRepository={shouldLoadCurrentRepo ? currentRepository : 'unset'}
      returnFocusRef={returnFocusRef}
      fallbackElement={fallbackElement}
    />
  )
}

export type CreateIssuePropsEntry = Omit<CreateIssueProps, 'issueFormRef' | 'currentRepository'>

type CreateIssueEntryWrapperProps = {
  createIssueProps: CreateIssuePropsEntry
  config: SafeOptionConfig
  currentRepository: CreateIssueDialogEntryQuery$data | null | 'unset'
  returnFocusRef?: RefObject<HTMLElement | null>
  fallbackElement?: React.ReactElement
}

const CreateIssueEntryWrapper = ({
  createIssueProps,
  config,
  currentRepository,
  returnFocusRef,
  fallbackElement,
}: CreateIssueEntryWrapperProps) => {
  const navigate = createIssueProps.navigate ?? noop
  const repository =
    currentRepository !== 'unset' && currentRepository && currentRepository.repository
      ? currentRepository.repository
      : null
  const issueFormRef = useRef<IssueFormRef>(null)

  const preselectedData = {
    repository: repository ?? undefined,
    template: undefined,
    parentIssue: config.issueCreateArguments?.parentIssue,
  }

  const props = {
    ...createIssueProps,
    issueFormRef,
    hideCreateMore: config.hideCreateMore,
  }

  let redirect = false
  const shouldEvaluateRedirect =
    repository?.hasIssuesEnabled && config.defaultDisplayMode !== DisplayMode.IssueDuplication
  if (shouldEvaluateRedirect) {
    // Redirect when no templates AND blank issues are enabled
    // hasAnyTemplates is always true if there are any contact links for the repo
    redirect = navigate !== noop && !repository.hasAnyTemplates && repository.isBlankIssuesEnabled
  }

  useEffect(() => {
    if (redirect && repository) {
      const searchParams = constructIssueCreateParams({
        includeRepository: false,
        repository,
        templateFileName: undefined,
        ...config.issueCreateArguments?.initialValues,
      })
      const url = `/${repository.nameWithOwner}/issues/new${searchParams ? `?${searchParams}` : ''}`
      navigate(url)
    }
  }, [navigate, redirect, repository, config.issueCreateArguments?.initialValues])

  if (redirect) {
    return null
  }

  // If we have a fallback element and data is still loading, render the fallback
  const isLoadingCurrentRepo = currentRepository === 'unset' ? false : currentRepository === null
  const isLoadingTopRepos =
    createIssueProps.topRepositories === 'unset' ? false : createIssueProps.topRepositories === null
  const isLoading = isLoadingCurrentRepo || isLoadingTopRepos
  if (isLoading) {
    return fallbackElement ? fallbackElement : null
  }

  return (
    <IssueCreateContextProvider
      optionConfig={config}
      overrideFallbackDisplaymode={DisplayMode.TemplatePicker}
      preselectedData={preselectedData}
    >
      <CreateIssueDialog
        currentRepository={currentRepository === 'unset' ? 'unset' : currentRepository?.repository || null}
        {...props}
        returnFocusRef={returnFocusRef}
      />
    </IssueCreateContextProvider>
  )
}
